import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { CheckBox } from '@/components/forms/CheckBox'
import { Select } from '@/components/forms/Select'
import { TextInput } from '@/components/forms/TextInput'
import { Toggle } from '@/components/forms/Toggle'
import { Modal } from '@/components/ui/Modal'
import { getDealers } from '@/services/dealers.service'
import { addUser, updateUser } from '@/services/user.service'
import { DashboardStoreContext } from '@/stores/DashboardStore'
import { toast } from '@/utils/helpers'

const DEFAULT_USER = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  dealerGroup: '',
  dealers: [],
  selectAll: false,
  hasFullGroupAccess: false,
}

const DealerUserModal = ({ setIsOpen, user = null, onSave, dealerGroupOptions }) => {
  // Context
  const { type } = useContext(DashboardStoreContext)

  // State
  const [loading, setLoading] = useState(false)
  const [dealerOptions, setDealerOptions] = useState([])

  const handleSuccess = (message) => {
    toast(message, 'success')
    reset()
    setIsOpen(false)
    onSave()
  }
  const handleError = (message) => toast(message, 'error')

  const selectedGroupId =
    user?.dealerUserProfile?.dealerGroups?.length > 0
      ? user.dealerUserProfile.dealerGroups[0].id
      : ''

  // Full group access = dealer groups set but dealers empty
  const hasFullGroupAccessValue =
    (user?.dealerUserProfile?.dealerGroups?.length > 0 &&
      user?.dealerUserProfile?.dealers?.length === 0) ||
    false

  const {
    watch,
    control,
    register,
    handleSubmit,
    reset,
    getValues,
    setError: setFormError,
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm({ defaultValues: DEFAULT_USER })

  // Function is triggered anytime the dealer group changes
  // Get the dealers associated with the selected dealer group.
  const fetchDealers = async (dealerGroup) => {
    if (!dealerGroup) return

    const response = await getDealers(`/dealers/all?group=${dealerGroup}`, setLoading, handleError)
    const options = response.map((dealer) => ({ ...dealer, label: dealer.name }))

    setDealerOptions(options)
  }

  // Initialize or update form values when modal opens or user data changes.
  useEffect(() => {
    if (!user) {
      reset(DEFAULT_USER)
      setDealerOptions([])
    } else {
      reset({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        role: user?.dealerUserProfile?.role || '',
        dealerGroup: selectedGroupId,
        dealers: [],
        selectAll: false,
        hasFullGroupAccess: hasFullGroupAccessValue,
      })

      fetchDealers(selectedGroupId)
    }
  }, [user])

  // - Initializes the 'dealers' field when editing a user, ensuring it reflects the current user's associated dealers.
  // - Runs when the `user` or `dealerOptions` change.
  // - Conditions checked:
  //   1. `user` must exist to confirm it's an edit operation.
  //   2. `dealerOptions` must be populated to provide selectable dealer data.
  //   3. `dealers` should be empty and `dealerGroup` should match the user's first dealer group to set dealers value.
  useEffect(() => {
    const dealers = getValues('dealers')
    const dealerGroup = getValues('dealerGroup')

    if (
      user &&
      dealerOptions.length > 0 &&
      dealers.length === 0 &&
      selectedGroupId === dealerGroup
    ) {
      let dealerIds = []

      if (hasFullGroupAccessValue) {
        // Full group access: pre-select all dealers in the group
        dealerIds = dealerOptions.map((option) => String(option.id))
      } else if (user && user.dealerUserProfile) {
        dealerIds = user.dealerUserProfile.dealers.map((dealer) => String(dealer.id))
      }

      setValue('dealers', dealerIds)
      setValue('selectAll', dealerIds.length === dealerOptions.length)
    }
  }, [user, dealerOptions])

  const roleOptions = useMemo(() => {
    const options = [
      { id: 'Service', label: 'Service' },
      { id: 'Management', label: 'Management' },
      { id: 'Administrator', label: 'Administrator' },
    ]

    return options
  }, [type])

  /**
   * Validates dealers selection
   * @param {Object} formData
   * @returns {boolean} whether validation passed
   */
  const validateDealers = (formData) => {
    if (formData.hasFullGroupAccess) {
      clearErrors('dealers')
      return true
    }
    if (formData.dealers.length === 0) {
      setFormError('dealers', {
        type: 'manual',
        message: 'Please select at least one dealer location.',
      })
      return false
    }
    clearErrors('dealers')
    return true
  }

  /**
   * Prepare payload for adding a user
   * @param {Object} formData
   */
  const prepareAddUserPayload = (formData) => {
    const { hasFullGroupAccess, dealers, firstName, lastName, email, role, dealerGroup } = formData

    return {
      firstName,
      lastName,
      email,
      dealerUserProfile: {
        dealerGroups: [dealerGroup],
        dealers: hasFullGroupAccess ? [] : dealers,
        role,
      },
    }
  }

  /**
   * Handles form submission
   * @param {Object} formData - The data entered in the form fields by the user.
   */
  const onSubmit = async (formData) => {
    if (!validateDealers(formData)) return

    const { hasFullGroupAccess, dealers, firstName, lastName, role, dealerGroup } = formData

    if (!user) {
      const formattedData = prepareAddUserPayload(formData)
      await addUser(formattedData, handleError, setLoading, handleSuccess)
    } else {
      const formattedData = {
        id: user.id,
        firstName,
        lastName,
        dealerUserProfile: {
          dealerGroups: [dealerGroup],
          dealers: hasFullGroupAccess ? [] : dealers,
          role,
        },
      }

      await updateUser(formattedData, handleError, setLoading, handleSuccess)
    }
  }

  const handleCheckboxChange = (event) => {
    const { checked, name, id } = event.target
    const dealers = getValues('dealers')
    clearErrors('dealers')

    if (id === 'selectAll') {
      if (checked) {
        setValue(
          'dealers',
          dealerOptions.map((option) => String(option.id)),
          { shouldValidate: true },
        )
        setValue('selectAll', true, { shouldValidate: true })
      } else {
        setValue('dealers', [], { shouldValidate: true })
        setValue('selectAll', false, { shouldValidate: true })
      }
    } else {
      let newDealersList
      if (checked) {
        newDealersList = [...dealers, name]
      } else {
        newDealersList = dealers.filter((dealerId) => dealerId !== name)
      }
      setValue('dealers', newDealersList, { shouldValidate: true })

      if (newDealersList.length === dealerOptions.length) {
        setValue('selectAll', true, { shouldValidate: true })
      } else {
        setValue('selectAll', false, { shouldValidate: true })
      }
    }
  }

  const actions = [
    {
      type: 'cancel',
      label: 'Cancel',
      onClick: () => {
        setIsOpen(false)
        reset()
        setDealerOptions([])
      },
    },
    {
      type: 'submit',
      label: user ? 'Save' : 'Invite User',
      onClick: handleSubmit(onSubmit),
    },
  ]

  if (!user) {
    actions.push({
      type: 'submit_and_continue',
      label: 'Invite and Add Another',
      onClick: handleSubmit(async (formData) => {
        if (!validateDealers(formData)) return

        const formattedData = prepareAddUserPayload(formData)

        // Use a custom success handler that resets the form and keeps the modal open
        await addUser(formattedData, handleError, setLoading, (message) => {
          toast(message, 'success')
          // Clear form and dealer options but keep modal open
          reset(DEFAULT_USER)
          setDealerOptions([])
          onSave()
        })
      }),
    })
  }

  return (
    <Modal
      open
      setOpen={setIsOpen}
      title={user ? 'Edit Dealer User' : 'Add Dealer User'}
      content={
        <div className="mb-2 mt-4 flex flex-col gap-4">
          <TextInput
            data-testid="dealerUserFirstName"
            error={errors.firstName && 'This field is required'}
            label="First Name"
            placeholder="First Name"
            id="firstName"
            required
            fullWidth
            className="px-4 py-2.5 shadow-sm"
            inputStyles="text-charcoal-900 text-base font-medium"
            {...register('firstName', { required: true })}
          />

          <TextInput
            data-testid="dealerUserLastName"
            error={errors.lastName && 'This field is required'}
            label="Last Name"
            id="lastName"
            placeholder="Last Name"
            required
            fullWidth
            inputStyles="text-charcoal-900 text-base font-medium"
            className="px-4 py-2.5 shadow-sm"
            {...register('lastName', { required: true })}
          />

          <TextInput
            data-testid="dealerUserEmail"
            error={errors.email && errors.email.message}
            label="Email"
            id="email"
            name="email"
            type="email"
            placeholder="Email"
            required
            fullWidth
            disabled={user}
            className="px-4 py-2.5 shadow-sm"
            inputStyles="text-charcoal-900 text-base font-medium"
            {...register('email', {
              required: 'This field is required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Entered value does not match email format',
              },
            })}
          />

          <Controller
            name="role"
            control={control}
            rules={{ required: true }}
            render={({ field: { value }, fieldState }) => (
              <Select
                dataTestId="dealerUserRole"
                name="role"
                id="role"
                label="Role"
                value={roleOptions.find((option) => option.id === value)}
                placeholder="Select an option"
                options={roleOptions}
                className="bg-white-light rounded-md border-white"
                inputTextClassName="text-charcoal-900 text-base font-medium"
                error={fieldState.error && 'This field is required'}
                onChange={(option) => {
                  setValue('role', option.id, { shouldValidate: true })
                }}
              />
            )}
          />

          <Controller
            name="dealerGroup"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Select
                dataTestId="dealerUserGroup"
                name="dealerGroup"
                id="dealerGroup"
                label="Dealer Group"
                value={dealerGroupOptions.find((option) => option.id === field.value)}
                placeholder="Select an option"
                options={dealerGroupOptions}
                className="bg-white-light rounded-md border-white"
                inputTextClassName="text-charcoal-900 text-base font-medium"
                error={fieldState.error && 'This field is required'}
                onChange={(option) => {
                  field.onChange(option.id)
                  fetchDealers(option.id)
                  setValue('dealers', [])
                  setValue('selectAll', false)
                  setValue('hasFullGroupAccess', false)
                }}
              />
            )}
          />

          {dealerOptions.length > 0 && (
            <div>
              <Controller
                name="hasFullGroupAccess"
                control={control}
                render={({ field: { value } }) => (
                  <Toggle
                    label="Full group access"
                    checked={value}
                    onChange={(checked) => {
                      setValue('hasFullGroupAccess', checked)
                      if (checked) {
                        setValue(
                          'dealers',
                          dealerOptions.map((option) => String(option.id)),
                        )
                        setValue('selectAll', true)
                        clearErrors('dealers')
                      } else {
                        setValue('dealers', [])
                        setValue('selectAll', false)
                      }
                    }}
                  />
                )}
              />
              <p className="text-charcoal-500 mt-1 text-xs">
                Includes access to all current and future dealers in the group.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 space-y-2">
            {dealerOptions.length > 1 && (
              <CheckBox
                disabled={
                  watch('hasFullGroupAccess') ||
                  (type === 'dealer' && _.some(dealerOptions, (d) => !d.isDealerManaged)) ||
                  (type === 'wpc' && _.some(dealerOptions, (d) => d.isDealerManaged))
                }
                disabledDescription={
                  // eslint-disable-next-line no-nested-ternary
                  watch('hasFullGroupAccess')
                    ? 'Controlled by full group access toggle.'
                    : type === 'dealer'
                    ? 'Some dealers are not dealer managed.'
                    : 'Some dealers are dealer managed.'
                }
                key="selectAll"
                label="Select All"
                name="selectAll"
                {...register('selectAll', {})}
                onChange={(event) => handleCheckboxChange(event, 'selectAll')}
                value={watch('selectAll')}
              />
            )}

            {dealerOptions.map((location) => (
              <CheckBox
                disabled={
                  watch('hasFullGroupAccess') ||
                  (type === 'dealer' && !location.isDealerManaged) ||
                  (type === 'wpc' && location.isDealerManaged)
                }
                disabledDescription={
                  // eslint-disable-next-line no-nested-ternary
                  watch('hasFullGroupAccess')
                    ? 'Controlled by full group access toggle.'
                    : type === 'dealer'
                    ? 'This dealer is not dealer managed.'
                    : 'This dealer is dealer managed.'
                }
                key={location.id}
                value={watch('dealers').includes(String(location.id))}
                label={location.label}
                name={location.id}
                onChange={(event) => handleCheckboxChange(event)}
              />
            ))}
          </div>

          <div
            className="mt-1 w-full bg-transparent px-2 py-1 text-center"
            aria-hidden={errors.dealers ? 'false' : 'true'}
          >
            <p className="text-error min-h-[24px] text-sm font-medium">
              {errors.dealers?.message || ' '}
            </p>
          </div>
        </div>
      }
      onClose={() => {
        setIsOpen(false)
        reset()
        setDealerOptions([])
      }}
      actions={actions}
      loading={loading}
      fullWidthButtons={false}
    />
  )
}

export default DealerUserModal

DealerUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
    role: PropTypes.string,
    dealerGroup: PropTypes.string,
    dealers: PropTypes.array,
  }),
  onSave: PropTypes.func.isRequired,
  dealerGroupOptions: PropTypes.array.isRequired,
}
